import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { HTMLProps, ReactNode, FC, forwardRef } from 'react';

import { ResizablePanel } from './ui';

export const InputInlineValidationErrorIcon = (): JSX.Element => (
  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
    <ExclamationCircleIcon
      className="h-5 w-5 text-red-500"
      aria-hidden="true"
    />
  </div>
);

const inputElementErrorClasses =
  'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500';

export const inputElementDefaultClasses =
  'border-gray-300 focus:border-blue-800 focus:ring-blue-800 placeholder:text-blue-600 text-blue';

export const inputElementClass = 'form-input block w-full rounded-md md:pr-10';

export const selectElementClass =
  'form-select block w-full rounded-md md:pr-10';

export const inputElementWithIconClass =
  'form-input block w-full rounded-md px-10';

export const InputValidationErrorMessage: FC<
  { errorMessage: string } & HTMLProps<HTMLParagraphElement>
> = ({ errorMessage, ...props }: { errorMessage: string }) => (
  <p className="mt-2 text-red-600" {...props}>
    {errorMessage}
  </p>
);

export const InputLeadingIconContainer = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => (
  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
    {children}
  </div>
);

export const getStatefulClass = (hasError: boolean): string =>
  hasError ? inputElementErrorClasses : inputElementDefaultClasses;

export const InputLabel: FC<
  { children: ReactNode } & HTMLProps<HTMLLabelElement>
> = ({ children, ...props }: { children: ReactNode }): JSX.Element => (
  <label {...props} className="block font-semibold text-mono-grey">
    {children}
  </label>
);

export const InputWrapper = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => (
  <div className="relative mt-1 rounded-md shadow-sm">{children}</div>
);

export const getClassNames = (...classes: string[]): string =>
  classes.filter(Boolean).join(' ');

export const InputTwoButtonToggle = ({
  InputOne,
  InputTwo,
  inputSelected,
}: {
  InputOne: {
    inputContent: ReactNode;
    inputProps: HTMLProps<HTMLInputElement>;
  };
  InputTwo: {
    inputContent: ReactNode;
    inputProps: HTMLProps<HTMLInputElement>;
  };
  inputSelected: null | 'one' | 'two';
}): JSX.Element => {
  const unselectedClass = inputElementDefaultClasses;
  const defaultClass =
    'relative inline-flex items-center px-4 py-2 border font-medium text-gray-700 hover:bg-gray-50 bg-mono-white';

  const selectedClass =
    'z-10 border-blue-800 outline-none ring-1 ring-blue-800';

  return (
    <span className="inline-flex">
      <InputLabel>
        <div
          className={`${
            inputSelected === 'one' ? selectedClass : unselectedClass
          } ${defaultClass} min-w-[75px] rounded-l-md`}
        >
          <input
            type="radio"
            name="unit"
            className="hidden"
            {...InputOne.inputProps}
          />
          {InputOne.inputContent}
        </div>
      </InputLabel>
      <InputLabel>
        <div
          className={`${
            inputSelected === 'two' ? selectedClass : unselectedClass
          } ${defaultClass} -ml-px min-w-[75px] rounded-r-md`}
        >
          <input
            type="radio"
            name="unit"
            className="hidden"
            {...InputTwo.inputProps}
          />
          {InputTwo.inputContent}
        </div>
      </InputLabel>
    </span>
  );
};

export const InputWithTrailingText = forwardRef<
  HTMLInputElement,
  { trailingText: string; isErrorState: boolean } & HTMLProps<HTMLInputElement>
>(
  (
    {
      trailingText,
      isErrorState,
      ...props
    }: {
      trailingText: string;
      isErrorState: boolean;
    } & HTMLProps<HTMLInputElement>,
    ref,
  ) => (
    <>
      <input
        ref={ref}
        {...props}
        className={`${getStatefulClass(!!isErrorState)} ${inputElementClass}`}
      />
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <span className="text-gray-500">{trailingText}</span>
      </div>
    </>
  ),
);

export const InputWithInLineValidationError = forwardRef<
  HTMLInputElement,
  { hasError: boolean } & HTMLProps<HTMLInputElement>
>(
  (
    { hasError, ...props }: { hasError: boolean } & HTMLProps<HTMLInputElement>,
    ref,
  ) => (
    <>
      <input
        ref={ref}
        {...props}
        className={`${getStatefulClass(hasError)} ${inputElementClass}`}
      />
      {hasError && <InputInlineValidationErrorIcon />}
    </>
  ),
);

export const InputWithLeadingIconAndInLineValidationError = forwardRef<
  HTMLInputElement,
  { hasError: boolean; children: ReactNode } & HTMLProps<HTMLInputElement>
>(
  (
    {
      hasError,
      children,
      ...props
    }: { hasError: boolean; children: ReactNode } & HTMLProps<HTMLInputElement>,
    ref,
  ) => (
    <>
      <InputLeadingIconContainer>{children}</InputLeadingIconContainer>
      <input
        ref={ref}
        {...props}
        className={`${getStatefulClass(hasError)} ${inputElementWithIconClass}`}
      />
      {hasError && <InputInlineValidationErrorIcon />}
    </>
  ),
);

export const ResizableInputErrorMessage = ({
  error,
}: {
  error?: string | null;
}): JSX.Element => (
  <ResizablePanel>
    {error && <InputValidationErrorMessage errorMessage={error} />}
  </ResizablePanel>
);

export const InputWrapperWithLabel: FC<
  {
    children: ReactNode;
    labelName: ReactNode | JSX.Element;
  } & HTMLProps<HTMLLabelElement>
> = ({
  children,
  labelName,
  ...props
}: {
  children: ReactNode;
  labelName: ReactNode | JSX.Element;
}): JSX.Element => (
  <>
    <InputLabel {...props}>{labelName}</InputLabel>
    <InputWrapper>{children}</InputWrapper>
  </>
);
